import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type {
  BoggleCompletedRun,
  BoggleWordOccurrence,
  BoggleWordProjectionSnapshot,
} from '~/boggle/progress/types'

export type BoggleAchievementCareerStats = {
  completedRunCount: number
  longWordCounts: Partial<Record<number, number>>
  maxRunWordCount: number
  ratingIdsReached: readonly BoggleRunSummaryRatingId[]
  totalWords: number
}

const RATING_RANK = new Map<BoggleRunSummaryRatingId, number>(
  BOGGLE_RUN_SUMMARY_RATINGS.map((rating, index) => [rating.id, index]),
)

function hasReachedRating(
  runRatingId: BoggleRunSummaryRatingId,
  targetRatingId: BoggleRunSummaryRatingId,
) {
  return (RATING_RANK.get(runRatingId) ?? -1) >= (RATING_RANK.get(targetRatingId) ?? -1)
}

function getRunWordCount(run: BoggleCompletedRun) {
  return run.acceptedWordTimeline?.length ?? run.submittedWords.length
}

export function buildBoggleAchievementCareerStats({
  completedRuns,
  wordProjections,
}: {
  completedRuns: readonly BoggleCompletedRun[]
  wordProjections: Pick<BoggleWordProjectionSnapshot, 'wordOccurrences'>
}): BoggleAchievementCareerStats {
  const ratingIdsReached = new Set<BoggleRunSummaryRatingId>()

  let maxRunWordCount = 0
  for (const run of completedRuns) {
    maxRunWordCount = Math.max(maxRunWordCount, getRunWordCount(run))

    for (const rating of BOGGLE_RUN_SUMMARY_RATINGS) {
      if (rating.id !== 'none' && hasReachedRating(run.ratingId, rating.id)) {
        ratingIdsReached.add(rating.id)
      }
    }
  }

  const longWordCounts: Partial<Record<number, number>> = {}
  for (const minLength of [7, 8, 9]) {
    longWordCounts[minLength] = wordProjections.wordOccurrences.filter(
      occurrence => occurrence.normalizedWord.length >= minLength,
    ).length
  }

  return {
    completedRunCount: completedRuns.length,
    longWordCounts,
    maxRunWordCount,
    ratingIdsReached: [...ratingIdsReached],
    totalWords: wordProjections.wordOccurrences.length,
  }
}

export function boggleCareerStatsHasReachedRating(
  careerStats: BoggleAchievementCareerStats,
  ratingId: BoggleRunSummaryRatingId,
) {
  return careerStats.ratingIdsReached.includes(ratingId)
}
